import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export default function SchemeMembers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scheme, setScheme] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);

  const reload = async () => {
    const id = getActiveSchemeId();
    if (!id) return;
    try {
      const [s, m] = await Promise.all([
        api.get(`/schemes/${id}`),
        api.get(`/schemes/${id}/members`),
      ]);
      setScheme(s.data);
      setMembers(m.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
      else setError(apiError(e));
    }
  };

  useEffect(() => {
    const id = getActiveSchemeId();
    if (!id) { navigate("/onboarding"); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const canEdit = (m) =>
    (members.find((x) => x.user_id === user?.user_id)?.role === "chair") ||
    (user?.user_id && m.user_id === user.user_id) ||
    (user?.system_role === "platform_admin");

  if (error)
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="rounded-lg p-4 text-sm" style={{ background: "var(--alert-light)", color: "var(--alert)" }}>
            {error}
          </div>
        </div>
      </AppShell>
    );
  if (!scheme) return <AppShell><div className="min-h-[40vh]" /></AppShell>;

  const roleOrder = { chair: 0, committee: 1, owner: 2 };
  const sorted = [...members].sort(
    (a, b) => roleOrder[a.role] - roleOrder[b.role] || a.email.localeCompare(b.email)
  );

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="scheme-members-page">
        <div className="mb-6">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
            <Users size={11} /> Member directory
          </span>
          <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>
            {scheme.name} — members
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
            {members.length} member{members.length === 1 ? "" : "s"} · {scheme.state}
          </p>
        </div>

        <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: "var(--border)" }}>
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr style={{ background: "var(--slate-light)" }}>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <tr key={m.membership_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`directory-row-${m.membership_id}`}>
                  <Td>{m.name || <span style={{ color: "var(--tagline-grey)" }}>—</span>}</Td>
                  <Td><span className="mono text-xs">{m.email}</span></Td>
                  <Td>
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{
                        background: m.role === "chair" ? "var(--blue-light)" : "var(--slate-light)",
                        color: m.role === "chair" ? "var(--blue-dark)" : "var(--slate)",
                      }}
                    >
                      {m.role}
                    </span>
                    {scheme?.state === "NSW" && (m.role === "chair" || m.role === "committee") && (
                      <TrainingBadge m={m} schemeId={scheme.scheme_id} canEdit={canEdit(m)} onSaved={reload} />
                    )}
                  </Td>
                  <Td>
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider"
                      style={{ color: m.status === "active" ? "var(--success)" : "var(--warning)" }}
                    >
                      {m.status}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

function TrainingBadge({ m, schemeId, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [url, setUrl] = useState("");
  const [provider, setProvider] = useState("");
  useEffect(() => {
    api.get(`/schemes/${schemeId}/memberships/${m.membership_id}/training`)
      .then((r) => setData(r.data)).catch(() => setData({}));
  }, [schemeId, m.membership_id]);
  if (!data) return null;
  const completedAt = data.training_completed_at;
  let pill = { label: "Training not recorded", bg: "#fee2e2", fg: "#991b1b" };
  if (completedAt) {
    const expiry = new Date(completedAt);
    expiry.setFullYear(expiry.getFullYear() + 1);
    const daysLeft = Math.floor((expiry - new Date()) / 86400000);
    if (daysLeft > 30) pill = { label: `Training current — expires ${expiry.toISOString().slice(0,10)}`, bg: "#d1fae5", fg: "#065f46" };
    else if (daysLeft > 0) pill = { label: `Training expires in ${daysLeft}d`, bg: "#fef3c7", fg: "#b45309" };
    else pill = { label: "Training overdue", bg: "#fee2e2", fg: "#991b1b" };
  }
  const save = async () => {
    try {
      await api.post(`/schemes/${schemeId}/memberships/${m.membership_id}/training`, {
        completed_at: date, certificate_url: url || null, provider: provider || null,
      });
      // Carry-over fix from Week 1: optimistically update local badge state so
      // the pill flips green immediately without a manual page reload.
      setData({
        training_completed_at: date,
        training_certificate_url: url || null,
        training_provider: provider || null,
      });
      setOpen(false); onSaved && onSaved();
    } catch (e) { /* silent */ }
  };
  return (
    <>
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => canEdit && setOpen(true)}
        className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
        style={{ background: pill.bg, color: pill.fg, cursor: canEdit ? "pointer" : "default" }}
        data-testid={`training-badge-${m.membership_id}`}
      >
        {pill.label}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="training-modal">
            <h3 className="font-semibold mb-3" style={{ color: "var(--navy)" }}>Record committee training — {m.name || m.email}</h3>
            <label className="text-xs block mb-1" style={{ color: "var(--slate)" }}>Completion date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border rounded px-2 py-1.5 mb-3" data-testid="training-date-input"/>
            <label className="text-xs block mb-1" style={{ color: "var(--slate)" }}>Certificate URL (optional)</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} className="w-full border rounded px-2 py-1.5 mb-3" placeholder="https://…" data-testid="training-url-input"/>
            <label className="text-xs block mb-1" style={{ color: "var(--slate)" }}>Provider (optional)</label>
            <input value={provider} onChange={(e) => setProvider(e.target.value)} className="w-full border rounded px-2 py-1.5 mb-4" placeholder="Strata Community Australia" data-testid="training-provider-input"/>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm rounded border">Cancel</button>
              <button onClick={save} className="px-3 py-1.5 text-sm rounded text-white" style={{ background: "var(--blue)" }} data-testid="training-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Th({ children }) {
  return (
    <th className="text-[10px] font-semibold uppercase tracking-wider px-4 py-2.5" style={{ color: "var(--slate)" }}>
      {children}
    </th>
  );
}

function Td({ children }) {
  return <td className="px-4 py-3" style={{ color: "var(--navy)" }}>{children}</td>;
}
